import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger } from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { DocumentSelectionType } from '../../../../components/Dropdown/DocumentDropdown'
import * as Yup from 'yup'
import DocumentCombobox, { DocumentComboboxProps } from '../../Combobox/DocumentCombobox'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { FormikConfig } from 'formik/dist/types'
import { FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import useSharePoint from '../../../../hooks/useSharePoint'
import { uploadDocument } from '../../../../helpers/documentHelper'
import { useDispatch } from 'react-redux'

export const getImportDocumentDialogBodySchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        folderId: Yup.string().required(translate('file.pivot.document.create.folderid.required')),
    })
}

export type ImportDocumentDialogBodyProps = {
    items?: File[]
    folders: FolderDriveItem[]
    callback?: () => void
}

export type ImportDocumentDialogBodyValues = {
    folderId: string
}

export default function ImportDocumentDialogBody(
    props: HammockFormDialogProps<ImportDocumentDialogBodyValues, ImportDocumentDialogBodyProps>,
): ReactElement {
    const axios = useAxios()
    const dispatch = useDispatch()
    const { sharePoint } = useSharePoint()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const { isSubmitting } = useFormikContext<ImportDocumentDialogBodyValues>()

    const dialogFormBodyProps = useMemo(() => props.dialogFormBodyProps() ?? ({} as ImportDocumentDialogBodyProps), [props])
    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const handleSubmit = useCallback<FormikConfig<ImportDocumentDialogBodyValues>['onSubmit']>(
        values => {
            Promise.all((dialogFormBodyProps.items || []).map(items => uploadDocument(axios, dispatch, values.folderId, items))).then(() => {
                dialogFormBodyProps.callback?.()
            })
        },
        [sharePoint],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    return (
        <DialogBody>
            <DialogTitle>{translate('file.pivot.document.import.title', { count: dialogFormBodyProps.items?.length || 0 })}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    <FormikComboboxField<ImportDocumentDialogBodyValues, DocumentComboboxProps>
                        required
                        labelKey={'file.pivot.document.import.form.folderid'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'folderId'}
                        component={DocumentCombobox}
                        componentProps={{
                            selectionType: DocumentSelectionType.FolderOnly,
                            entities: dialogFormBodyProps.folders,
                        }}
                    />
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button appearance="primary" type={'submit'} disabled={isSubmitting}>
                    {translate(`file.pivot.document.import.form.submit`, { count: dialogFormBodyProps.items?.length || 0 })}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary" disabled={isSubmitting}>
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
